import { useEffect, useRef, useState } from "react";
import { Button, Input, Typography, Space } from "antd";
import { Download, FileText, Link, QrCode, SendHorizontal } from "lucide-react";
import { Spinner, Offcanvas } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import '../../assets/css/FollowersList.css';
import BiolinkQRCode from "../biolink-page/BiolinkQRCode";
import { chatLockUnlockStatus } from "../../redux/slice/ChatLockUnlockStatus";

const ChatBox = ({ show, onHide, follower, chatFollower, qrCode2 }) => {
    const [messages, setMessages] = useState([]);
    const [inputVal, setInputVal] = useState("");
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const listEndRef = useRef(null);
    const didInitialScroll = useRef(false);
    const navigate = useNavigate();
    const currentUserId = localStorage.getItem('id');
    const [qrShow, setQRShow] = useState(false)
    const [qrCode, setQrCode] = useState(false)
    const dispatch = useDispatch();

    const handleQR = (data) => {
        setQrCode(data)
        setQRShow(true)
    }


    // Fetch messages from API
    const fetchMessages = async () => {
        const senderId = follower?.user ? follower?.user?._id : follower?._id || chatFollower?._id;
        if (!senderId || !currentUserId) return { data: [] };

        try {
            setFetching(true);
            const res = await axios({
                method: 'GET',
                url: process.env.REACT_APP_API_URL + `chat_list`,
                params: {
                    sender_id: senderId,
                    receiver_id: currentUserId
                },
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`
                }
            });

            if (res?.status === 200 && res?.data?.data) {
                // Process messages to determine if they're from current user or other user
                const processedMessages = res.data.data.map((msg) => {
                    const isFromCurrentUser = msg.sender_id?._id === currentUserId || msg.sender_id === currentUserId;
                    return {
                        ...msg,
                        id: msg._id || msg.id,
                        show: isFromCurrentUser ? "right" : "left",
                        timestamp: msg.createdAt || msg.timestamp || new Date().toISOString(),
                        date: msg.date,
                        time: msg.time // Preserve time from API (format: "HH:mm:ss")
                    };
                });
                return { data: processedMessages };
            }
            return { data: [] };
        } catch (error) {
            console.error("Failed to fetch messages:", error);
            if (error?.response?.status === 401) {
                localStorage.clear();
                navigate('/login');
            }
            return { data: [] };
        } finally {
            setFetching(false);
        }
    };

    // Send message API call
    const sendMessage = async (text) => {
        const receiverId = follower?.user ? follower?.user?._id : follower?._id || chatFollower?._id;
        if (!receiverId || !currentUserId || !text.trim()) {
            toast.error('Cannot send message', { position: "bottom-right" });
            return;
        }

        try {
            setLoading(true);
            const res = await fetch(process.env.REACT_APP_API_URL + "send_chat_message", {
                method: "POST",
                body: JSON.stringify({
                    sender_id: currentUserId,
                    receiver_id: receiverId,
                    message: text.trim()
                }),
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("token")}`
                }
            });

            const responseData = await res.json();

            if (res.status === 200 || res.status === 201) {
                // toast.success('Message sent!', { position: "bottom-right" });
                // Refresh messages after sending
                const updatedMessages = await fetchMessages();
                setMessages(updatedMessages.data || []);
            } else {
                toast.error(responseData?.message || 'Failed to send message', { position: "bottom-right" });
            }
        } catch (error) {
            console.error("Failed to send message:", error);
            toast.error('Failed to send message. Please try again.', { position: "bottom-right" });
        } finally {
            setLoading(false);
        }
    };

    // Load messages when chat opens or follower changes
    useEffect(() => {
        const senderId = follower?.user ? follower?.user?._id : follower?._id || chatFollower?._id;
        if (!show || !senderId) {
            setMessages([]);
            didInitialScroll.current = false;
            return;
        }

        let isMounted = true;
        let firstLoad = true;

        const loadMessages = async () => {
            try {
                const res = await fetchMessages();
                if (isMounted) {
                    setMessages(res?.data || []);

                    // Scroll to bottom on first load
                    if (firstLoad && res?.data?.length > 0) {
                        setTimeout(() => {
                            listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
                            firstLoad = false;
                            didInitialScroll.current = true;
                        }, 100);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch messages:", err);
            }
        };

        dispatch(chatLockUnlockStatus({ navigate })).then((res) => {
            if (res?.payload?.status === 200) {
                localStorage.setItem('chat_lock_unlock_status', res?.payload?.data?.data?.is_chat_locked)
            }
        })

        loadMessages();
        // Poll for new messages every 3 seconds
        const id = setInterval(loadMessages, 3000);

        return () => {
            isMounted = false;
            clearInterval(id);
        };
    }, [show, follower?.user ? follower?.user?._id : follower?._id, chatFollower?._id, currentUserId]);

    // Auto-scroll when new messages arrive
    useEffect(() => {
        if (messages.length > 0) {
            setTimeout(() => {
                listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
            }, 100);
        }
    }, [messages.length]);

    const handleSend = async () => {
        if (!inputVal.trim() || loading) return;
        const messageToSend = inputVal.trim();
        setInputVal("");
        await sendMessage(messageToSend);
    };


    const formatTime = (timeValue, timestamp) => {
        // If time is already provided from API (HH:mm:ss format), convert to 12-hour format
        if (timeValue && typeof timeValue === 'string' && timeValue.includes(':')) {
            const timeParts = timeValue.split(':');
            if (timeParts.length >= 2) {
                let hours = parseInt(timeParts[0], 10);
                const minutes = timeParts[1];
                const ampm = hours >= 12 ? 'PM' : 'AM';

                // Convert to 12-hour format
                hours = hours % 12;
                hours = hours ? hours : 12; // 0 should be 12

                // Return in 12-hour format with AM/PM
                return `${hours}:${minutes} ${ampm}`;
            }
            return timeValue; // Return as is if format is unexpected
        }
        // Fallback to formatting timestamp
        if (timestamp) {
            const date = new Date(timestamp);
            return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
        }
        return '';
    };

    // Helper functions for follower profile
    const getProfileImage = (f) => {

        const user = f?.user ? f?.user : f || {}
        const profile = user?.profile?.length ? user?.profile?.[0] : user?.profile || {}
        const profilePhoto = profile?.profile_photo || user?.profile_photo || f?.profile_photo || null;

        if (profilePhoto) {
            return profilePhoto.startsWith('http')
                ? profilePhoto
                : process.env.REACT_APP_BASE_URL + profilePhoto
        }
        return null
    }

    const getInitials = (name) => {
        if (!name || name === 'undefined' || name === 'null') return 'U'
        // Trim and filter empty strings to handle trailing or double spaces
        const names = name.trim().split(' ').filter(n => n.length > 0)
        if (names.length >= 2) {
            return (names[0][0] + names[names.length - 1][0]).toUpperCase()
        }
        return name.trim().substring(0, 2).toUpperCase()
    }

    const profileImage = follower ? getProfileImage(follower) : (chatFollower?.profile_photo || null)

    const rawTitle = follower?.profile?.length ? follower?.profile?.[0]?.title :
        follower?.user?.profile ? follower?.user?.profile?.title : null;
    const rawName = follower?.user?.name || follower?.name || chatFollower?.name;

    const validTitle = (rawTitle && rawTitle !== 'undefined' && rawTitle !== 'null') ? rawTitle : null
    const validName = (rawName && rawName !== 'undefined' && rawName !== 'null') ? rawName : null

    const userName = (validTitle || validName || 'User').trim()
    const initials = getInitials(userName)

    return (
        <>
            <Offcanvas
                show={show}
                onHide={onHide}
                placement="end"
                className="chat-offcanvas"
            >
                <Offcanvas.Header className="chat-offcanvas-header">
                    <div className="chat-header-content">
                        {(follower || chatFollower) && (
                            <div className="chat-profile-section">
                                {(profileImage || chatFollower?.profile_photo) ? (
                                    <img
                                        src={profileImage || chatFollower?.profile_photo}
                                        alt={userName || 'User'}
                                        className="chat-profile-image"
                                        onError={(e) => {
                                            e.target.style.display = 'none'
                                            e.target.nextSibling.style.display = 'flex'
                                        }}
                                    />
                                ) : null}
                                <div
                                    className="chat-profile-avatar"
                                    style={{ display: (profileImage || chatFollower?.profile_photo) ? 'none' : 'flex' }}
                                >
                                    {initials || chatFollower?.initials || 'U'}
                                </div>
                                <div className="chat-profile-info">
                                    <h5 className="chat-user-name">{userName || chatFollower?.name || follower?.user?.name}</h5>
                                    <div className='d-flex align-items-center'>
                                        <a
                                            className="btn btn_qr_code"
                                            href={process.env.REACT_APP_BASE_URL + `x/${follower?.user ? follower?.user?.user_name : follower?.user_name || chatFollower?.user_name}`}
                                            title="Copy URL"
                                            target="_blank"
                                        >
                                            <Link size={15} className='' />
                                        </a>
                                        <button
                                            className='ms-2 btn btn_qr_code'
                                            onClick={() => handleQR(follower ? follower : qrCode2)}
                                            title="QR Code"
                                        >
                                            <QrCode size={15} className='' />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        <button
                            className="chat-close-btn"
                            onClick={onHide}
                            aria-label="Close"
                        >
                            <FaTimes />
                        </button>
                    </div>
                </Offcanvas.Header>
                <Offcanvas.Body className="chat-offcanvas-body">
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            height: '100%',
                        }}
                    >
                        {/* Messages */}
                        <div style={{ flex: 1, overflowY: "auto", padding: 10 }}>
                            {fetching && messages.length === 0 ? (
                                <div style={{ textAlign: "center", padding: 20 }}>
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : messages.length === 0 ? (
                                <div style={{ textAlign: "center", padding: 20, color: "#888" }}>
                                    <Typography.Text type="secondary">No messages yet. Start a conversation!</Typography.Text>
                                </div>
                            ) : (
                                messages.map((m) => (
                                    <div
                                        key={m.id || m._id}
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: m.show === "right" ? "flex-end" : "flex-start",
                                            marginBottom: 12,
                                        }}
                                    >
                                        <div
                                            style={{
                                                maxWidth: "70%",
                                                padding: "8px 12px",
                                                borderRadius: 12,
                                                background: m.show === "right" ? "#436a53" : "#f0f0f0",
                                                color: m.show === "right" ? "white" : "black",
                                                wordWrap: "break-word",
                                            }}
                                        >
                                            {m.msg_type === "text" || !m.msg_type ? (
                                                <Typography.Text
                                                    className="fw-medium"
                                                    style={{ color: m.show === "right" ? "white" : "black" }}
                                                >
                                                    {m.message}
                                                </Typography.Text>
                                            ) : (
                                                <Space>
                                                    <FileText size={16} />
                                                    <Typography.Text
                                                        className="fw-medium"
                                                        style={{ color: m.show === "right" ? "white" : "black" }}
                                                    >
                                                        {'Document attached '}
                                                    </Typography.Text>
                                                    <Button
                                                        size="small"
                                                        type="link"
                                                        href={m.message?.startsWith('http') ? m.message : process.env.REACT_APP_BASE_URL + m.message}
                                                        target="_blank"
                                                        icon={<Download size={16} />}
                                                    >
                                                        Download
                                                    </Button>
                                                </Space>
                                            )}
                                        </div>
                                        {/* Timestamp */}
                                        <Typography.Text
                                            type="secondary"
                                            style={{
                                                fontSize: 10,
                                                marginTop: 4,
                                                color: "#888",
                                                textAlign: m.show === "right" ? "right" : "left",
                                            }}
                                        >
                                            {formatTime(m.time, m.timestamp || m.createdAt)}
                                        </Typography.Text>
                                    </div>
                                ))
                            )}
                            <div ref={listEndRef} />
                        </div>

                        {/* Input */}
                        <div
                            style={{
                                padding: 10,
                                borderTop: "1px solid #ddd",
                                display: "flex",
                                gap: 8,
                            }}
                        >

                            {
                                localStorage.getItem('chat_lock_unlock_status') === 'locked' ?
                                    <Typography.Text type="danger" className="mb-0">
                                        Chat is locked. You cannot send messages.
                                    </Typography.Text>
                                    :
                                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                                        <Input
                                            placeholder="Type a message..."
                                            value={inputVal}
                                            maxLength={150}
                                            onChange={(e) => setInputVal(e.target.value)}
                                            onPressEnter={handleSend}
                                        />
                                        <div style={{
                                            fontSize: '10px',
                                            color: inputVal.length >= 150 ? 'red' : '#888',
                                            textAlign: 'right'
                                        }}>
                                            {inputVal.length}/150
                                        </div>
                                    </div>}
                          {localStorage.getItem('chat_lock_unlock_status') === 'locked' ? "":  <Button
                                type="primary"
                                icon={loading ? <Spinner animation="grow" size="sm" /> : <SendHorizontal size={16} />}
                                disabled={loading}
                                onClick={handleSend}
                            >
                                Send
                            </Button>}
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
            <BiolinkQRCode qrShow={qrShow} setQRShow={setQRShow} qrCode={qrCode} />
        </>

    );
};

export default ChatBox;